import React, { useCallback, useState } from "react";
import { FaSearch } from "react-icons/fa";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import { CommunityDTO } from "utils/helpers/models/shared/community.dto";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import useStore from "hooks/useStore";
import Sidebar from "./component/Sidebar";
import ChatWindow from "./component/ChatWindow";
import MessageInput from "./component/MessageInput";
import AttendeeBreadcrumb, { AttendeBreadcrumbLink } from "components/breadcrumbs/AttendeeBreadcrumb";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import CommunityNotFoundGif from "assets/images/gifs/community.gif";

const Communities: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.home },
    { title: "Communities", path: siteRoutes.communities },
  ];

  const [searchTitle, setSearchTitle] = useState("");
  const [currentCommunity, setCurrentCommunity] = useState<CommunityDTO | null>(
    null
  );
  const [messages, setMessages] = useState<MessageDTO[]>([]);
  const [showChat, setShowChat] = useState(false);
  const [communitiesCount, setCommunitiesCount] = useState<number>(0);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(true);

  const { getUserProfilePic } = useStore();
  const userProfilePic = getUserProfilePic();

  const handleOpenChat = useCallback((community: CommunityDTO) => {
    setCurrentCommunity(community);
    setShowChat(true);
    setMessages([]);
  }, []);

  const handleBackToList = useCallback(() => {
    setShowChat(false);
    setCurrentCommunity(null);
    setMessages([]);
  }, []);

  // Check if the community/event has ended - memoized
  const isEventEnded = useCallback(
    (community: CommunityDTO | null): boolean => {
      if (
        !community ||
        !community.endDateTime ||
        community.endDateTime.length === 0
      ) {
        return false;
      }
      // Get the latest end date
      const latestEndDate = new Date(
        Math.max(
          ...community.endDateTime.map((date) => new Date(date).getTime())
        )
      );
      return latestEndDate < new Date();
    },
    []
  );

  const handleSearch = useCallback(() => {
    // Sidebar reacts to searchTitle prop; no-op here
  }, [searchTitle]);

  return (
    <div className="container max-w-full overflow-x-hidden px-2 md:px-4">
      <Breadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl text-blackie font-bold my-4">Community</h2>
      {/* Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-2 sm:gap-0">
        <div className="flex items-center border border-gray-300 rounded-lg px-4 py-2 h-[50px] w-full sm:flex-1">
          <FaSearch className="text-gray-400 mr-2 flex-shrink-0" />
          <input
            type="text"
            placeholder="Find Conversation"
            className="w-full outline-none bg-transparent text-gray-700 min-w-0"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
          />
        </div>
        <button
          className="px-4 py-2 bg-primaryBlueBg text-white rounded-full whitespace-nowrap sm:ml-3 h-[50px] sm:h-auto"
          onClick={handleSearch}
        >
          Search
        </button>
      </div>

      <div className="h-[calc(100vh-200px)] min-h-0 relative">
        {communitiesCount === 0 && (
          <div className="flex items-center justify-center h-full min-h-[50vh] absolute inset-0 z-10 bg-white">
            <CustomMessageDisplay
              show={true}
              title="No Active Communities"
              slogan="You don’t have an active community. Create an experience to start one."
              image={CommunityNotFoundGif}
              imageAlt="No communities found"
              className="py-16"
              imageClassName="w-[150px] h-[120px]"
              titleClassName="text-2xl font-bold text-blackie"
              sloganClassName="text-gray-500 text-base max-w-md"
            />
          </div>
        )}
        <div className={`flex flex-col md:flex-row my-6 md:my-10 h-full gap-4 md:gap-0 min-w-0 ${communitiesCount === 0 ? 'opacity-0' : ''}`}>
          <div className={`${showChat ? "hidden md:block" : "block"} md:flex-shrink-0 min-w-0 h-full flex flex-col`}>
            <Sidebar
              activeCommunityId={currentCommunity?.eventData?.eventId ?? null}
              onSelect={handleOpenChat}
              searchTitle={searchTitle}
              onCommunitiesChange={setCommunitiesCount}
              onLoadingChange={setIsLoadingCommunities}
            />
          </div>

          <div
            className={`flex-1 flex flex-col md:px-3 overflow-y-auto overflow-x-hidden custom-scrollbar min-w-0 h-full ${
              showChat ? "block" : "hidden md:flex"
            }`}
          >
          <ChatWindow
            community={currentCommunity}
            messages={messages}
            setMessages={setMessages}
            userProfilePic={userProfilePic}
            onBack={handleBackToList}
            isEventEnded={isEventEnded(currentCommunity)}
          />

            {currentCommunity && (
              <>
                {isEventEnded(currentCommunity) ? (
                  <div className="flex items-center justify-center p-6 flex-shrink-0">
                    <div className="text-center text-gray-600 bg-gray-100 rounded-lg p-4 max-w-full">
                      <p className="text-base sm:text-lg font-medium break-words">
                        Community conversation are now archived. Grateful to
                        have shared this space with you.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-shrink-0">
                    <MessageInput
                      community={currentCommunity}
                      messages={messages}
                      setMessages={setMessages}
                      onMessageAppended={(m) =>
                        setMessages((prev) => [...prev, m])
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Communities;
